import { CostType } from './commission'

/**
 * What a cost is, which side of the business it belongs to, and how much GST is in it.
 *
 * Three of Winnie's items land in this one file, because they are three views of the same
 * fact about a cost line:
 *
 *   "Define cost categories - split acquisition cost (buying fees) from direct cost
 *    (inspection, freight, reconditioning where applicable)"
 *   "The GST is not calculated correctly"
 *   "every subsequent invoice - inspection, freight/pickup, reconditioning, detailing -
 *    gets entered against that same VIN"
 *
 * Keeping them together means a category cannot be added in one place and forgotten in
 * the other two.
 */

/**
 * Acquisition or direct.
 *
 * The distinction is not cosmetic. An acquisition cost is part of buying the car - it is
 * incurred whether or not the car ever sells, and it belongs in the landed cost from the
 * moment of purchase. A direct cost is spent getting a specific car ready or moved, and
 * arrives afterwards, invoice by invoice. Reporting them as one number is what makes a
 * vehicle's true cost impossible to see until long after it matters.
 */
export type CostGroup = 'Acquisition' | 'Direct' | 'Other'

export interface CostCategory {
  value: string
  label: string
  group: CostGroup
  /** How this maps onto the commission spec's cost types. Null means it needs a person. */
  costType: CostType | null
  /** Whether a supplier invoice for this normally carries GST. */
  usuallyHasGst: boolean
}

/**
 * The categories the database will accept. The stored value is the snake_case key - there
 * is a check constraint behind it, and the first cut of the split-invoice form sent the
 * pretty label instead and had every row rejected.
 *
 * `detailing` is now its own category. It was absent for a long time because the
 * `expenses.category` check constraint refused the value - verified by probing the live
 * database, not assumed - so offering it would have failed at save time rather than at
 * review time. The constraint was extended on 9 Sep and the value confirmed accepted, so
 * reconditioning goes back to meaning reconditioning.
 */
export const COST_CATEGORIES: CostCategory[] = [
  {
    value: 'buying_fee',
    label: 'Buying fee',
    group: 'Acquisition',
    costType: 'PurchasePrice',
    usuallyHasGst: true,
  },
  {
    value: 'finance_payout',
    label: 'Finance payout',
    group: 'Acquisition',
    // Paying out a seller's financier is settling their loan, not buying a service.
    // There is no GST on it, and defaulting it to 10% would overstate the input credit.
    costType: 'PurchasePrice',
    usuallyHasGst: false,
  },
  {
    value: 'inspection',
    label: 'Inspection',
    group: 'Direct',
    costType: 'Inspection',
    usuallyHasGst: true,
  },
  {
    value: 'transport',
    label: 'Transport / freight',
    group: 'Direct',
    // Freight in and freight out sit on opposite sides of the margin and the word does
    // not say which, so the person entering it has to choose. Guessing here would put a
    // cost on the wrong side of a commission calculation.
    costType: null,
    usuallyHasGst: true,
  },
  {
    value: 'reconditioning',
    label: 'Reconditioning',
    group: 'Direct',
    costType: 'Reconditioning',
    usuallyHasGst: true,
  },
  {
    value: 'detailing',
    label: 'Detailing',
    group: 'Direct',
    // Detailing is preparation for sale, the same side of the margin as reconditioning, and
    // it maps onto the same cost type. It is a separate CATEGORY so a detailing invoice can
    // be reported on by itself - which is what Winnie asked for - without splitting the
    // commission arithmetic, which does not care about the distinction.
    costType: 'Reconditioning',
    usuallyHasGst: true,
  },
  {
    value: 'ppsr',
    label: 'PPSR',
    group: 'Direct',
    // A government search fee is not a taxable supply.
    costType: 'Inspection',
    usuallyHasGst: false,
  },
  {
    /* Winnie, 13 Sep: the lot load fee, $250 by default, applied to every vehicle the
       moment it becomes ready for sale. Its own category so it can be seen, corrected and
       reported on rather than hiding inside "Other" alongside everything else. */
    value: 'lot_load',
    label: 'Lot load fee',
    group: 'Acquisition',
    costType: 'LotLoad',
    usuallyHasGst: true,
  },
  {
    value: 'other',
    label: 'Other',
    group: 'Other',
    costType: null,
    usuallyHasGst: true,
  },
]

const BY_VALUE: Record<string, CostCategory> = {}
for (const c of COST_CATEGORIES) BY_VALUE[c.value] = c

export function categoryOf(value: string | null | undefined): CostCategory | null {
  if (!value) return null
  return BY_VALUE[String(value).toLowerCase().trim()] || null
}

export function groupOf(value: string | null | undefined): CostGroup {
  const c = categoryOf(value)
  return c ? c.group : 'Other'
}
/**
 * The category to file an auto-created accrual under.
 *
 * `expenses.category` is NOT NULL and sits behind a check constraint, so an accrual written
 * without one simply does not save. That is what happened the first time this path was driven
 * with a rate card in place: nothing had ever tried to insert one before. The cost type is the
 * thing an accrual actually knows about itself, so the mapping runs that way round here.
 *
 * Freight collapses back onto the single `transport` category, which cannot say in or out.
 * That is not a loss - the row also carries an explicit `cost_type`, and `costTypeOf` prefers
 * it over the category.
 */
export function categoryForCostType(costType: CostType | null | undefined): string {
  switch (costType) {
    case 'PurchasePrice':
      return 'buying_fee'
    case 'Inspection':
      return 'inspection'
    case 'FreightIn':
    case 'FreightOut':
      return 'transport'
    case 'Reconditioning':
      return 'reconditioning'
    default:
      return 'other'
  }
}

export const GROUP_LABEL: Record<CostGroup, string> = {
  Acquisition: 'Acquisition cost',
  Direct: 'Direct cost',
  Other: 'Other',
}

/* ------------------------------------------------------------------------------------
 * GST
 * ---------------------------------------------------------------------------------- */

export const GST_DIVISOR = 11

/**
 * The GST inside a GST-inclusive amount.
 *
 * This is the whole of "the GST is not calculated correctly". Until now the Cost of Sales
 * form asked for the amount and then asked for the GST as a second, unrelated number,
 * with nothing computing it and nothing checking it. Type $489 and $48.90 and the form
 * accepted it - but $48.90 is ten per cent added ON TOP of $489, and a supplier's tax
 * invoice for $489 contains $44.45. The error is small on one line and compounds through
 * the BAS.
 *
 * Every other money figure in this platform is GST-inclusive and has the tax taken out at
 * one eleventh - `gstComponent`, the GST Register, the P&L, the customer tax invoice
 * after the 3 September fix. A cost line is no different.
 */
export function gstOn(amountInclusive: number | string | null | undefined): number {
  const n = Number(amountInclusive)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.round((n / GST_DIVISOR) * 100) / 100
}

/** The ex-GST value of a GST-inclusive amount, derived from the rounded GST so the two
 *  always add back to the total. Round each separately and they disagree by a cent. */
export function exGst(amountInclusive: number | string | null | undefined): number {
  const n = Number(amountInclusive)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.round((n - gstOn(n)) * 100) / 100
}

export interface GstCheck {
  ok: boolean
  code: 'ok' | 'zero_but_expected' | 'exceeds_amount' | 'looks_like_added_on_top' | 'not_one_eleventh'
  expected: number
  message: string | null
}

/**
 * Is the GST somebody typed plausible for the amount they typed?
 *
 * Deliberately a warning and not a block, except where it is arithmetically impossible.
 * Real invoices are mixed: part GST-free, rounded oddly by a supplier's own system, or
 * genuinely zero because the supplier is not registered. Refusing those would push people
 * into entering a wrong number to get past the form, which is worse than a warning they
 * can read and overrule.
 *
 * The one case worth naming precisely is ten per cent added on top, because that is the
 * mistake the old form invited and it looks almost right.
 */
export function checkGst(
  amountInclusive: number | string | null | undefined,
  gstEntered: number | string | null | undefined,
  category?: string | null,
): GstCheck {
  const amount = Number(amountInclusive) || 0
  const gst = Number(gstEntered) || 0
  const expected = gstOn(amount)
  const cat = categoryOf(category)

  if (gst > amount) {
    return {
      ok: false,
      code: 'exceeds_amount',
      expected,
      message: 'The GST is larger than the amount. One of the two is wrong.',
    }
  }

  const tenPercentOnTop = Math.round(amount * 0.1 * 100) / 100
  if (amount > 0 && gst > expected && Math.abs(gst - tenPercentOnTop) <= 0.02) {
    return {
      ok: false,
      code: 'looks_like_added_on_top',
      expected,
      message:
        'That is 10% added on top of the amount. On a GST-inclusive invoice of ' +
        aud(amount) +
        ' the GST is ' +
        aud(expected) +
        ' - one eleventh, not one tenth.',
    }
  }

  if (gst === 0) {
    if (cat && !cat.usuallyHasGst) return { ok: true, code: 'ok', expected: 0, message: null }
    if (amount === 0) return { ok: true, code: 'ok', expected: 0, message: null }
    return {
      ok: true,
      code: 'zero_but_expected',
      expected,
      message:
        'No GST on a ' +
        (cat ? cat.label.toLowerCase() : 'cost') +
        ' invoice of ' +
        aud(amount) +
        '. That is right if the supplier is not registered for GST - otherwise it is ' +
        aud(expected) +
        '.',
    }
  }

  if (Math.abs(gst - expected) > 0.02) {
    return {
      ok: true,
      code: 'not_one_eleventh',
      expected,
      message:
        'Expected ' +
        aud(expected) +
        ' on a GST-inclusive ' +
        aud(amount) +
        '. Leave it if the invoice is partly GST-free.',
    }
  }

  return { ok: true, code: 'ok', expected, message: null }
}

/**
 * Apportion one invoice's GST across its lines, remainder on the last line.
 *
 * The parts have to add back to the invoice's GST exactly. Split three ways with a naive
 * round and a cent evaporates, and the ledger then carries a discrepancy nobody can find.
 */
export function apportionGst(gstTotal: number, lineAmounts: number[]): number[] {
  const cents = Math.round((Number(gstTotal) || 0) * 100)
  const amounts = lineAmounts.map((a) => Math.round((Number(a) || 0) * 100))
  const total = amounts.reduce((s, a) => s + a, 0)
  if (!cents || !total) return lineAmounts.map(() => 0)
  const out: number[] = []
  let running = 0
  for (let i = 0; i < amounts.length; i++) {
    if (i === amounts.length - 1) {
      out.push((cents - running) / 100)
    } else {
      const share = Math.round((cents * amounts[i]) / total)
      running += share
      out.push(share / 100)
    }
  }
  return out
}

function aud(n: number): string {
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
